import {
  ChevronDownIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Internal } from '@gleamer/types';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MY_TASKS_LIMIT, useMyTasks } from '../../hooks/useMyTasks';
import { PageTitle } from '../atoms/PageTitle';
import { TableBodyTd, TableHeadTh } from '../atoms/Table';
import { ErrorAlert } from '../molecules/ErrorAlert';
import { FinishedTaskAlert } from '../molecules/FinishedTaskAlert';
import {
  byProgress,
  FinishedChip,
  formatDay,
  isFinished,
  progressShare,
  RoleChip,
  TaskAction,
  TaskCard,
} from '../molecules/tasks/TaskCard';

type LabelTask = Internal.LabelTask;

type Role = 'all' | 'labeling' | 'review';
/** All: what is left to do, then the finished ones, folded. To do and Done: only those. */
type Status = 'all' | 'todo' | 'done';
type Sort = 'progress' | 'newest' | 'oldest' | 'name';
type View = 'cards' | 'list';

const SORTS: Record<
  Sort,
  { label: string; compare: (a: LabelTask, b: LabelTask) => number }
> = {
  progress: { label: 'Most advanced first', compare: byProgress },
  newest: {
    label: 'Newest first',
    compare: (a, b) => b.createdAt.localeCompare(a.createdAt),
  },
  oldest: {
    label: 'Oldest first',
    compare: (a, b) => a.createdAt.localeCompare(b.createdAt),
  },
  name: { label: 'Name A–Z', compare: (a, b) => a.name.localeCompare(b.name) },
};

/** The tasks the toolbar keeps: of the role picked, matching the search in their id, name or description. */
export function filterTasks(
  tasks: LabelTask[],
  role: Role,
  search: string,
  sort: Sort
) {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  return tasks
    .filter(task => role === 'all' || task.reviewer === (role === 'review'))
    .filter(task => {
      const text =
        `${task.taskId} ${task.name} ${task.description ?? ''}`.toLowerCase();
      return words.every(word => text.includes(word));
    })
    .sort(SORTS[sort].compare);
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: React.ReactNode; title?: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex gap-0.5 rounded-lg bg-dl-sunken p-[3px]"
    >
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          title={option.title}
          onClick={() => onChange(option.value)}
          className={classNames(
            'flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-lg font-semibold tabular-nums transition',
            value === option.value
              ? 'bg-dl-surface text-dl-text shadow-sm'
              : 'text-dl-muted hover:text-dl-text'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** How far the user is, in a row: a short bar and the figures. */
function RowProgress({ task }: { task: LabelTask }) {
  const p = task.progress;
  if (!p) {
    return <span className="text-dl-faint">—</span>;
  }
  const share = progressShare(task) ?? 0;
  return (
    <span className="flex min-w-[10rem] flex-col gap-1.5">
      <span
        aria-hidden
        className="h-1.5 overflow-hidden rounded-full bg-dl-track"
      >
        <span
          className={classNames(
            'block h-full rounded-full',
            share >= 1 ? 'bg-dl-success' : 'bg-dl-primary'
          )}
          style={{ width: `${Math.round(share * 100)}%` }}
        />
      </span>
      <span className="text-base tabular-nums text-dl-muted">
        <strong className="font-semibold text-dl-text">{p.labeled}</strong>{' '}
        done, {p.left} left
      </span>
    </span>
  );
}

function TasksList({ tasks }: { tasks: LabelTask[] }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-dl-border bg-dl-surface">
      <table className="w-full table-auto">
        <thead>
          <tr>
            <TableHeadTh>Task</TableHeadTh>
            <TableHeadTh>Role</TableHeadTh>
            <TableHeadTh>Description</TableHeadTh>
            <TableHeadTh>Progress</TableHeadTh>
            <TableHeadTh>Created</TableHeadTh>
            <TableHeadTh />
          </tr>
        </thead>
        <tbody>
          {tasks.map(task => (
            <tr key={task.taskId} className="group relative">
              <TableBodyTd>
                <span className="block text-lg font-semibold">{task.name}</span>
                <span className="font-mono text-sm text-dl-faint">
                  #{task.taskId}
                </span>
              </TableBodyTd>
              <TableBodyTd>
                <RoleChip reviewer={task.reviewer} />
              </TableBodyTd>
              <TableBodyTd className="max-w-md">
                <span className="line-clamp-2 text-lg text-dl-muted">
                  {task.description || '—'}
                </span>
              </TableBodyTd>
              <TableBodyTd className="whitespace-nowrap">
                <RowProgress task={task} />
              </TableBodyTd>
              <TableBodyTd className="whitespace-nowrap text-lg text-dl-muted">
                {formatDay(task.createdAt)}
              </TableBodyTd>
              <TableBodyTd className="text-right">
                {isFinished(task) ? (
                  <FinishedChip />
                ) : (
                  <TaskAction task={task} variant="outline" stretched />
                )}
              </TableBodyTd>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The user's tasks, as a list (or cards). All of them are read at once (useMyTasks), so the search, the role and
 * status filters and the sort work in the page; they are kept in the URL, as the home's figures link to a role
 * or a status.
 */
export function MyTasks() {
  const tasksQuery = useMyTasks();
  const [params, setParams] = useSearchParams();
  const [showFinished, setShowFinished] = useState(false);
  const role = (
    ['labeling', 'review'].includes(params.get('role'))
      ? params.get('role')
      : 'all'
  ) as Role;
  const status = (
    ['todo', 'done'].includes(params.get('status'))
      ? params.get('status')
      : 'all'
  ) as Status;
  const sort = (
    params.get('sort') in SORTS ? params.get('sort') : 'progress'
  ) as Sort;
  const view = (params.get('view') === 'cards' ? 'cards' : 'list') as View;
  const search = params.get('q') ?? '';

  const set = (name: string, value: string, fallback: string) => {
    const next = new URLSearchParams(params);
    if (value === fallback) {
      next.delete(name);
    } else {
      next.set(name, value);
    }
    setParams(next, { replace: true });
  };

  const all = tasksQuery.data?.content ?? [];
  const shown = filterTasks(all, role, search, sort);
  const toDo = shown.filter(task => !isFinished(task));
  const finished = shown.filter(isFinished);
  // the role and the search narrow what the status counts
  const showToDo = status !== 'done';
  const showDone = status !== 'todo';
  const tasks = (list: LabelTask[]) =>
    view === 'cards' ? (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {list.map(task => (
          <TaskCard key={task.taskId} task={task} />
        ))}
      </div>
    ) : (
      <TasksList tasks={list} />
    );
  const finishedTaskId = params.has('finished')
    ? parseInt(params.get('finished'), 10)
    : null;
  const finishedTask = finishedTaskId
    ? all.find(task => task.taskId === finishedTaskId)
    : null;
  const total = tasksQuery.data?.totalElements ?? 0;

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col gap-5">
      <PageTitle
        title="My tasks"
        className="items-baseline !justify-start gap-3"
      >
        {tasksQuery.isSuccess && (
          <span className="text-xl tabular-nums text-dl-faint">
            {shown.length === all.length
              ? `${all.length} assigned`
              : `${shown.length} of ${all.length} assigned`}
          </span>
        )}
      </PageTitle>
      {finishedTask && <FinishedTaskAlert task={finishedTask} />}
      {params.has('error') && <ErrorAlert />}

      <div className="flex flex-wrap items-center gap-3">
        <label className="relative min-w-[16rem] flex-1">
          <span className="sr-only">Search tasks</span>
          <MagnifyingGlassIcon
            aria-hidden
            className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-dl-faint"
          />
          <input
            type="search"
            value={search}
            placeholder="Search by name, description or id"
            onChange={e => set('q', e.target.value, '')}
            className="h-[42px] w-full rounded-lg border-0 bg-dl-surface pl-10 pr-4 text-lg text-dl-text ring-1 ring-dl-border placeholder:text-dl-faint focus:outline-none focus:ring-2 focus:ring-dl-primary"
          />
        </label>
        <Segmented<Role>
          label="Role"
          value={role}
          onChange={value => set('role', value, 'all')}
          options={[
            { value: 'all', label: <>All {count(all)}</> },
            {
              value: 'labeling',
              label: <>Labeling {count(all.filter(t => !t.reviewer))}</>,
            },
            {
              value: 'review',
              label: <>Review {count(all.filter(t => t.reviewer))}</>,
            },
          ]}
        />
        <Segmented<Status>
          label="Status"
          value={status}
          onChange={value => set('status', value, 'all')}
          options={[
            { value: 'all', label: 'All' },
            { value: 'todo', label: <>To do {count(toDo)}</> },
            { value: 'done', label: <>Done {count(finished)}</> },
          ]}
        />
        <label className="relative flex items-center gap-2 text-lg text-dl-muted">
          Sort
          <select
            value={sort}
            onChange={e => set('sort', e.target.value, 'progress')}
            className="h-[42px] appearance-none rounded-lg border-0 bg-dl-surface pl-3 pr-10 text-lg font-medium text-dl-text ring-1 ring-dl-border focus:outline-none focus:ring-2 focus:ring-dl-primary"
          >
            {Object.entries(SORTS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            aria-hidden
            className="pointer-events-none absolute right-3 h-4 w-4 text-dl-faint"
          />
        </label>
        <Segmented<View>
          label="View"
          value={view}
          onChange={value => set('view', value, 'list')}
          options={[
            {
              value: 'list',
              title: 'List',
              label: (
                <>
                  <ListBulletIcon className="h-5 w-5" />
                  <span className="sr-only">List</span>
                </>
              ),
            },
            {
              value: 'cards',
              title: 'Cards',
              label: (
                <>
                  <Squares2X2Icon className="h-5 w-5" />
                  <span className="sr-only">Cards</span>
                </>
              ),
            },
          ]}
        />
      </div>

      {total > all.length && (
        <p className="text-lg text-dl-muted">
          Showing your {MY_TASKS_LIMIT.toLocaleString()} newest tasks out of{' '}
          {total.toLocaleString()}.
        </p>
      )}

      {tasksQuery.isLoading && (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3].map(i => (
            <div
              key={i}
              aria-hidden
              className="h-16 animate-pulse rounded-xl bg-dl-surface"
            />
          ))}
        </div>
      )}
      {tasksQuery.isError && (
        <p role="alert" className="text-dl-danger">
          Could not load your tasks: {(tasksQuery.error as Error)?.message}
        </p>
      )}
      {tasksQuery.isSuccess && all.length === 0 && (
        <EmptyState
          title="No task assigned yet"
          text="A data manager adds you to a task; it shows here as soon as they do."
        />
      )}
      {tasksQuery.isSuccess && all.length > 0 && shown.length === 0 && (
        <EmptyState
          title="No task matches"
          text="Try another search, or show every role."
        />
      )}
      {shown.length > 0 && showToDo && (
        <section aria-label="To do" className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold text-dl-text">
            To do{' '}
            <span className="font-normal tabular-nums text-dl-faint">
              ({toDo.length})
            </span>
          </h2>
          {toDo.length > 0 ? (
            tasks(toDo)
          ) : (
            <EmptyState
              title="Nothing left to do"
              text="Every task shown here is finished: well done."
            />
          )}
        </section>
      )}
      {shown.length > 0 && status === 'done' && (
        <section aria-label="Done" className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold text-dl-text">
            Done{' '}
            <span className="font-normal tabular-nums text-dl-faint">
              ({finished.length})
            </span>
          </h2>
          {finished.length > 0 ? (
            tasks(finished)
          ) : (
            <EmptyState
              title="Nothing finished yet"
              text="A task shows here once you have labelled or reviewed all of its items."
            />
          )}
        </section>
      )}
      {finished.length > 0 && showDone && status !== 'done' && (
        <section aria-label="Finished" className="flex flex-col gap-4">
          <button
            type="button"
            aria-expanded={showFinished}
            onClick={() => setShowFinished(open => !open)}
            className="flex items-center gap-2 self-start text-xl font-semibold text-dl-text"
          >
            <ChevronDownIcon
              aria-hidden
              className={classNames('h-5 w-5 text-dl-faint transition', {
                '-rotate-90': !showFinished,
              })}
            />
            Finished{' '}
            <span className="font-normal tabular-nums text-dl-faint">
              ({finished.length})
            </span>
          </button>
          {showFinished && <div className="opacity-80">{tasks(finished)}</div>}
        </section>
      )}
    </div>
  );
}

/** A filter's count, quieter than its label. */
function count(tasks: LabelTask[]): React.ReactNode {
  return <span className="font-medium text-dl-faint">{tasks.length}</span>;
}

function EmptyState({ title, text }: { title: string; text: string }) {
  return (
    <div className="rounded-2xl border border-dl-border bg-dl-surface px-6 py-12 text-center">
      <p className="text-xl font-semibold text-dl-text">{title}</p>
      <p className="mt-2 text-lg text-dl-muted">{text}</p>
    </div>
  );
}

export default MyTasks;
